import { Component, Input, SimpleChanges, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';

interface FilterTypes {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-custom-numbers-autofilter',
  standalone: true,
  imports: [MatFormFieldModule, MatSelectModule, MatInputModule, FormsModule, MatRadioModule],
  templateUrl: './custom-numbers-autofilter.component.html',
  styleUrl: './custom-numbers-autofilter.component.css'
})
export class CustomNumbersAutofilterComponent implements OnChanges, OnInit {
  @Input() columnValues: number[] = [];
  @Input() column: any;
  @Output() applyNumberFilter = new EventEmitter<any>();
  @Output() clearNumberFilter = new EventEmitter<any>();
  numberValues: number[] = [];
  numberInput1: any = null;
  numberInput2: any = null;
  filter1Types: FilterTypes[] = [
    { value: 'equals', viewValue: '=' },
    { value: 'greaterThanOrEqual', viewValue: '>=' },
    { value: 'lessThanOrEqual', viewValue: '<=' },
    { value: 'greaterThan', viewValue: '>' },
    { value: 'lessThan', viewValue: '<' }
  ];
  filter2Types: FilterTypes[] = this.filter1Types.filter(t => t.value !== 'equals');

  selectedToggle: string = 'and';

  selectedFilterType: string = 'equals';

  filter1Number: number | null = null;
  filter1Type: string = 'greaterThanOrEqual';
  filter2Number: number | null = null;
  filter2Type: string = 'lessThanOrEqual';
  ifFilterApplied: boolean = false;

  ngOnInit() {
    this.updateNumberValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columnValues'] && changes['columnValues'].currentValue) {
      this.updateNumberValues();
    }
  }

  selectedToggleChange(event: any) {
    this.selectedToggle = event.value;
  }

  updateNumberValues() {
    if (!Array.isArray(this.columnValues) || this.columnValues.length === 0) {
      this.numberValues = [];
      return;
    }
    // Round up to nearest integer, remove duplicates, and sort
    this.numberValues = Array.from(new Set(this.columnValues.map(v => Math.round(v)))).sort((a, b) => a - b);
    //assign initial values
    if (this.numberValues.length === 0) {
      this.filter1Number = null;
      this.filter2Number = null;
      return;
    }
  }

  onFilterTypeChange(event: any) {
    this.selectedFilterType = event.value;
  }

  getFilteredValues() {
    // Helper functions for filter logic
    const filterFn = (val: number, type: string, num: number | null) => {
      val = Math.round(val); // Round value to nearest integer
      if (num === null) return false;
      if (type === 'equals') return val === num;
      if (type === 'greaterThan') return val > num;
      if (type === 'lessThan') return val < num;
      if (type === 'greaterThanOrEqual') return val >= num;
      if (type === 'lessThanOrEqual') return val <= num;
      return true;
    };

    if (this.filter1Type === 'equals' || this.filter2Number === null) {
      return this.columnValues.filter(val => filterFn(val, this.filter1Type, this.filter1Number));
    }

    if (this.selectedToggle === 'and') {
      // Return values that satisfy both conditions
      return this.columnValues.filter(
        val =>
          filterFn(val, this.filter1Type, this.filter1Number) && filterFn(val, this.filter2Type, this.filter2Number)
      );
    } else {
      // Return values that satisfy at least one condition
      return this.columnValues.filter(
        val =>
          filterFn(val, this.filter1Type, this.filter1Number) || filterFn(val, this.filter2Type, this.filter2Number)
      );
    }
  }

  onApplyClick() {
    let filtered = this.getFilteredValues();
    this.clearNumberFilter.emit(this.column);
    this.ifFilterApplied = true;
    this.applyNumberFilter.emit({
      options: [{ _value: filtered, _selected: true }],
      source: { _value: filtered },
      column: this.column
    });
  }

  onClearClick() {
    this.ifFilterApplied = false;
    this.filter1Number = null;
    this.filter2Number = null;
    this.filter1Type = 'greaterThanOrEqual';
    this.filter2Type = 'lessThanOrEqual';
    this.selectedToggle = 'and';
    this.clearNumberFilter.emit(this.column);
  }

  preventDecimal(event: KeyboardEvent) {
    if (event.key === '.' || event.key === 'Decimal') {
      event.preventDefault();
    }
  }
}
